import React, { useEffect, useState, useRef } from "react";
import { IoIosPersonAdd } from "react-icons/io";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/Auth";
import { RotatingLines } from "react-loader-spinner";
import ExportData from "../../components/common/ExportData";
import { ChevronLeft, ChevronRight, Plus, Pencil, X, Trash2 } from "lucide-react";
import DebouncedSearchInput from "../../components/common/DebouncedSearchInput";
import SearchableSupplierDropdown from "../../components/common/SearchableSupplierDropdown";
import Swal from "sweetalert2";

const SupplierInvoice = () => {
  const { requestWithAuth } = useAuth();
  const debounceTimerRef = useRef(null);
  const [supplierList, setSupplierList] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [tableData, setTableData] = useState([]);

  // Filter states
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [selectedNarration, setSelectedNarration] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Edit modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editForm, setEditForm] = useState({
    invoice_date: "",
    invoice_amount: "",
    supplier: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  const fetchInvoiceList = async (page_size, search_query, current_page) => {
    setLoading(true);
    try {
      let url = `/supplier/invoice?page=${current_page}&pageSize=${page_size}&search=${search_query}`;

      if (selectedSupplier) {
        url += `&supplier=${selectedSupplier}`;
      }
      if (selectedNarration) {
        url += `&invoice_narration=${selectedNarration}`;
      }
      if (startDate) {
        url += `&start_date=${startDate}`;
      }
      if (endDate) {
        url += `&end_date=${endDate}`;
      }

      const data = await requestWithAuth(url, "GET");
      setSupplierList(data?.results);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil(data?.count / pageSize));
    } catch (error) {
      console.error("Error fetching invoice data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async () => {
    setLoading(true);
    try {
      let url = `/supplier/invoice?paginated=false&search=${search}`;

      if (selectedSupplier) {
        url += `&supplier=${selectedSupplier}`;
      }
      if (selectedNarration) {
        url += `&invoice_narration=${selectedNarration}`;
      }
      if (startDate) {
        url += `&start_date=${startDate}`;
      }
      if (endDate) {
        url += `&end_date=${endDate}`;
      }

      const data = await requestWithAuth(url, "GET");
      setTableData(data);
    } catch (error) {
      console.error("Error fetching invoice data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSuppliers = async () => {
    try {
      const data = await requestWithAuth(`/supplier?paginated=false`, "GET");
      setSuppliers(data);
    } catch (error) {
      console.error("Error fetching suppliers data:", error);
    }
  };

  const filterInvoiceList = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const clearFilters = () => {
    setSelectedSupplier("");
    setSelectedNarration("");
    setStartDate("");
    setEndDate("");
    setSearch("");
    setCurrentPage(1);
  };

  const openEditModal = (invoice) => {
    setEditingInvoice(invoice);
    setEditForm({
      invoice_date: invoice?.invoice_date || "",
      invoice_amount: invoice?.invoice_amount || "",
      supplier: invoice?.supplier_id || invoice?.supplier || "",
    });
    setEditError("");
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingInvoice(null);
    setEditError("");
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError("");
    try {
      await requestWithAuth(
        `/supplier/invoice/${editingInvoice?.id}/details/`,
        "PATCH",
        editForm
      );
      Swal.fire({
        icon: "success",
        title: "Invoice updated successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      closeEditModal();
      fetchInvoiceList(pageSize, search, currentPage);
      getTableData();
    } catch (error) {
      console.error("Error updating invoice:", error);
      setEditError("Failed to update invoice. Please try again.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    Swal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: "You are about to delete this supplier invoice. This action cannot be undone!",
      showDenyButton: true,
      confirmButtonText: "Yes, delete it!",
      denyButtonText: "No, cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await requestWithAuth(
            `/supplier/invoice/${invoiceId}/details/`,
            "DELETE"
          );
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Invoice deleted successfully",
            showConfirmButton: false,
            timer: 1500,
          });
          fetchInvoiceList(pageSize, search, currentPage);
          getTableData();
        } catch (error) {
          console.error("Error deleting invoice:", error);
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Failed to delete the invoice. Please try again.",
          });
        }
      }
    });
  };

  const handleDeleteAllInvoices = async () => {
    Swal.fire({
      icon: "warning",
      title: "Delete All Invoices?",
      text: `Are you sure you want to delete all ${tableData?.length || supplierList?.length} invoices? This action cannot be undone!`,
      showDenyButton: true,
      confirmButtonText: "Yes, delete all!",
      denyButtonText: "No, cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const invoicesToDelete = tableData?.length > 0 ? tableData : supplierList;
          if (!invoicesToDelete || invoicesToDelete.length === 0) {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "No invoices to delete.",
            });
            return;
          }

          const deletePromises = invoicesToDelete.map((invoice) =>
            requestWithAuth(`/supplier/invoice/${invoice.id}/details/`, "DELETE")
          );

          await Promise.all(deletePromises);

          Swal.fire({
            icon: "success",
            title: "Success",
            text: "All invoices deleted successfully",
            showConfirmButton: false,
            timer: 1500,
          });
          setCurrentPage(1);
          fetchInvoiceList(pageSize, search, currentPage);
          getTableData();
        } catch (error) {
          console.error("Error deleting all invoices:", error);
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Failed to delete all invoices. Some might not have been deleted.",
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${
            number === currentPage
              ? "bg-blue-500 text-white shadow-sm"
              : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  useEffect(() => {
    fetchInvoiceList(pageSize, search, currentPage);
    getTableData();
  }, [
    search,
    currentPage,
    pageSize,
    selectedSupplier,
    selectedNarration,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    getSuppliers();

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <>
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Inventory</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Supplier Invoice
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">
                Supplier Invoice
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {supplierList?.length > 0 && (
                <button
                  onClick={handleDeleteAllInvoices}
                  className="bg-red-500 hover:bg-red-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
                >
                  <Trash2 size={20} />
                  Delete All Invoices
                </button>
              )}
              <Link
                to="/inventory/invoice/new"
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
              >
                <Plus size={20} />
                New Invoice with Stock
              </Link>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Filters Section */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Filter Invoices
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <SearchableSupplierDropdown
                suppliers={suppliers}
                value={selectedSupplier}
                onChange={setSelectedSupplier}
                label="Supplier"
              />

              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Narration
                </label>
                <select
                  value={selectedNarration}
                  onChange={(e) => {
                    setSelectedNarration(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="">All Narrations</option>
                  <option value="Supply">Supply</option>
                  <option value="Credit Note">Credit Note</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                />
              </div>
            </div>

            {(selectedSupplier ||
              selectedNarration ||
              startDate ||
              endDate) && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>

          {/* Filters Bar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <ExportData tableData={tableData} fileName="Supplier Invoice" />
                <span className="text-sm text-gray-600">Show</span>
                <select
                  onChange={(e) => setPageSize(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  value={pageSize}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>

              <DebouncedSearchInput
                onSearch={filterInvoiceList}
                placeholder="Search invoices..."
                delay={500}
                className="flex-1 max-w-md"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span>Loading invoices...</span>
                </div>
              </div>
            ) : supplierList?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Invoice Number
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Supplier Name
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Invoice Amount
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Invoice Date
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Narration
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {supplierList?.map((supplier, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {supplier?.invoice_number}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-900">
                        {supplier?.supplier}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {supplier?.invoice_amount || "Not Assigned"}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {supplier?.invoice_date}
                      </td>
                      <td className="py-4 px-6">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            supplier?.invoice_narration === "Supply"
                              ? "bg-green-100 text-green-800"
                              : "bg-orange-100 text-orange-800"
                          }`}
                        >
                          {supplier?.invoice_narration}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/inventory/invoice/${supplier?.id}/details`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                          >
                            View Details
                          </Link>
                          <button
                            onClick={() => openEditModal(supplier)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors"
                          >
                            <Pencil size={13} />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteInvoice(supplier?.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                          >
                            <Trash2 size={13} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <svg
                    className="mx-auto h-12 w-12"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <p className="text-gray-500 text-sm">
                  No supplier invoices found
                </p>
                {search && (
                  <p className="text-gray-400 text-xs mt-1">
                    Try adjusting your search terms
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {supplierList?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${
                      currentPage > 1
                        ? "text-gray-600 hover:bg-gray-100"
                        : "text-gray-300 cursor-not-allowed"
                    }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${
                      currentPage < totalPages
                        ? "text-gray-600 hover:bg-gray-100"
                        : "text-gray-300 cursor-not-allowed"
                    }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

      {/* Edit Invoice Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeEditModal}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Edit Invoice</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {editingInvoice?.invoice_number}
                </p>
              </div>
              <button
                onClick={closeEditModal}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleEditSubmit} className="p-6 space-y-5">
              {editError && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                  <span>{editError}</span>
                </div>
              )}

              {/* Supplier */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Supplier
                </label>
                <SearchableSupplierDropdown
                  suppliers={suppliers}
                  value={editForm.supplier}
                  onChange={(val) =>
                    setEditForm((prev) => ({ ...prev, supplier: val }))
                  }
                  label=""
                />
              </div>

              {/* Invoice Date */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Invoice Date
                </label>
                <input
                  type="date"
                  value={editForm.invoice_date}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      invoice_date: e.target.value,
                    }))
                  }
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-white"
                />
              </div>

              {/* Invoice Amount */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Invoice Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.invoice_amount}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      invoice_amount: e.target.value,
                    }))
                  }
                  required
                  placeholder="0.00"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-white"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeEditModal}
                  disabled={editLoading}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2"
                >
                  {editLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default SupplierInvoice;
